import React, { memo, useEffect, useState } from "react";
import { Form, Input } from "antd";
import { Button, Input as BP2Input } from "../../../BP2Components";
import InputMask from "react-input-mask";
import { useIntl } from "react-intl";
import { useDebouncedCallback } from "use-debounce";
import { useFormContext } from "../../../../context/FormContext/FormContext";
import PropTypes from "prop-types";
import {
   handleChangedRelatedField,
   handleFieldAlert,
   handleGridTechname,
   handleInputBlockade,
   renderDevInfo,
} from "../../GeneralScripts";
import { getAllFieldsWithAlerts } from "../../../../util/Scripts";
import "./TextField.less";
import { useSelector } from "react-redux";
import { handleTemplateAlert } from "../../../../util/Scripts";
import DedicatedListButton from "../../FormUtils/DedicatedListButton";
import FieldInfo from "../FieldInfo/FieldInfo";
import { useMessageItem } from "../../../../context/MessageItemContext/MessageItemContext";

const TextField = memo(
   ({
      element,
      form,
      disabled,
      isVisible,
      formId,
      insideGrid = false,
      initialValue,
      isRequired,
      rowDataId,
      setChangelogData,
   }) => {
      const intl = useIntl();
      const {
         formData,
         changeFieldPendingStatus,
         fieldsInfo,
         setFieldsInfo,
         fieldOptions,
         initialData,
         fieldEnumMap,
      } = useFormContext();

      const { setIsAnyFieldInFormLoading } = useMessageItem();

      const { getFieldDecorator } = form;
      const { gridFormRefs, formInfo } = useSelector(({ formsv2 }) => formsv2);
      const [value, setValue] = useState(getDefaultValue() || "");
      const [infoText, setInfoText] = useState(null);

      const advancedSearchListId = +(
         //TO DO: Give back some english lessons
         (
            element?.FormLoContent?.advansed_search_list_id ||
            element?.FormLoContent?.dedicated_list_value
         )
      );
      const allFieldsWithAlerts = getAllFieldsWithAlerts(formData?.forms_data?.tab?.[0]?.elements);
      const handleDebouncedChange = useDebouncedCallback(() => {
         handleChangedRelatedField(
            element,
            form,
            setFieldsInfo,
            allFieldsWithAlerts,
            formId,
            intl,
            formData._formKey,
            rowDataId,
            changeFieldPendingStatus,
            fieldEnumMap,
         );
         element.hasAlert &&
            handleFieldAlert(
               element.id,
               element.tech_name,
               form,
               formData._formKey,
               setFieldsInfo,
               fieldEnumMap,
            );
      }, 200);

      const handleValueChange = (e) => {
         const value = e?.target?.value ?? e ?? "";
         const lastValue = form.getFieldValue(element.tech_name);
         if (lastValue !== value) {
            setIsAnyFieldInFormLoading(true);
            setValue(value);
            form.setFieldsValue({ [element.tech_name]: value });
            handleDebouncedChange();
         }
      };

      const changeFieldValue = (value) => {
         setValue(value);
         form.setFieldsValue({ [element.tech_name]: value });
      };

      function getDefaultValue() {
         let initVal = initialValue ?? element.value;
         let stringVal = String(initVal);

         if (initVal && stringVal.includes("{") && stringVal.includes("::")) {
            const [parentFormId, techName] = stringVal
               .replaceAll("{", "")
               .replaceAll("}", "")
               .split("::");

            const parsedParentFormId = Number(parentFormId);

            if (isNaN(parsedParentFormId)) {
               return initVal;
            }

            const parentForm = Object.values(formInfo).find(
               (elem) => elem?.formId === parsedParentFormId && elem?.mainFormRef?.getFieldValue,
            );

            if (parentForm) {
               try {
                  initVal = parentForm.mainFormRef.getFieldValue(techName);
               } catch (err) {
                  console.log(err);
               }
            }
         }

         return initVal;
      }

      const handleNumberInputChange = (e, isBlur = false) => {
         const lastValue = form.getFieldValue(element.tech_name);

         const value = e?.target?.value;
         if (lastValue !== value) {
            if (!isBlur) {
               setIsAnyFieldInFormLoading(true);
            }
            const numericValue = Number(value);
            if (!isNaN(numericValue)) {
               setValue(numericValue);
            }

            form.setFieldsValue({ [element.tech_name]: value });
            handleDebouncedChange();
         }
      };

      const blurHandle = () => {
         if (isVisible(element)) {
            setChangelogData && setChangelogData(element.tech_name, value, element.label);
         }
      };

      useEffect(() => {
         // Changelog
         if (isVisible(element)) {
            const initVal = initialValue ?? element.value;
            if (initVal) {
               setChangelogData && setChangelogData(element.tech_name, initVal);
            }
         }
      }, []);

      useEffect(() => {
         const options = fieldOptions[element.tech_name];
         if (!options?.length) return;

         const newValue = options[0]?.label;
         if (newValue == null || newValue === "") return;

         const current = form.getFieldValue(element.tech_name) ?? "";
         const exists = options.some((option) => String(option.label) === String(current));

         if (current === "" || !exists) {
            changeFieldValue(newValue);
            if (isVisible(element) && setChangelogData) {
               setChangelogData(element.tech_name, newValue);
            }
         }
      }, [fieldOptions[element.tech_name]]);

      //After response from handleChangeRelatedField we need to update state value by value from form.getFieldValue
      useEffect(() => {
         const formValue = form.getFieldValue(element.tech_name);

         if (formValue !== undefined && formValue !== value) {
            setValue(formValue);
         }
      }, [form.getFieldValue(element.tech_name), value]);

      // Sum needs certain techname
      /* useEffect(() => {
         try {
            if (
               element.domysl.includes("::") &&
               !insideGrid &&
               !element.domysl.includes("math::")
            ) {
               const sumColTechname = element.domysl.split("::")[1].slice(0, -2);
               const columnCells = document.querySelectorAll(`span[id="${sumColTechname}"]`);
               if (!columnCells.length) {
                  form.setFieldsValue({ [element.tech_name]: 0 });
                  return;
               }
               const sum = Array.from(columnCells).reduce((acc, element) => {
                  const cellValue = !isNaN(Number(element.innerHTML))
                     ? Number(element.innerHTML)
                     : Number(element.querySelector("input")?.value);
                  return acc + cellValue;
               }, 0);
               form.setFieldsValue({ [element.tech_name]: sum });
            }
         } catch (e) {
            form.setFieldsValue({ [element.tech_name]: 0 });
         }
      }, [gridFormRefs?.length]); */

      // we made this to prevent saving form after clicking enter, for example when changing the estimate.
      const handleKeyDown = (event) => {
         if (event.key === "Enter") {
            event.preventDefault();
         }
      };

      const translatedLabel = element.label;

      const infoClass =
         fieldsInfo[element.tech_name] && fieldsInfo[element.tech_name] !== ""
            ? "text-field-info"
            : "";

      useEffect(() => {
         const infoText = fieldsInfo[element.tech_name];
         setInfoText(infoText);
         handleTemplateAlert(element.tech_name, infoText);
      }, [fieldsInfo[element.tech_name]]);

      return (
         <Form.Item
            key={element.tech_name}
            name={element.tech_name}
            label={!insideGrid && translatedLabel}
            className={`${element?.type}-field`}
            onKeyDown={handleKeyDown}
            style={{
               display: !isVisible(element) && !window.devLogged ? "none" : "",
               gridArea: handleGridTechname(element.tech_name),
               opacity: isVisible(element) ? "1" : "0.5",
            }}
            data-extraButtons={element.FormLoContent.seeking_out_type === "3"}
            // required in getFieldDecorator is not working properly on text fields
            required={isRequired(element)}
         >
            {getFieldDecorator(element.tech_name, {
               rules: [
                  {
                     required: isRequired(element),
                     message: intl.formatMessage({ id: "field.required" }),
                  },
               ],
               initialValue: getDefaultValue(),
            })}
            {element.mask === "99:99" ? (
               <Input
                  className={`text-field__input field_${element.tech_name} _type_${element?.type}_type_ _id_${element.id}_id_ ${infoClass} has-alert-${element.hasAlert}`}
                  type="time"
                  key={"text_" + element.tech_name}
                  disabled={disabled}
                  onChange={handleValueChange}
                  value={value}
                  onBlur={blurHandle}
                  {...(disabled ||
                     (!isVisible(element) && {
                        onKeyDown: handleInputBlockade,
                        onPaste: handleInputBlockade,
                        onCut: handleInputBlockade,
                     }))}
               />
            ) : !!element.mask && element.mask !== "99:99" ? (
               <InputMask
                  mask={element.mask}
                  disabled={disabled}
                  onChange={handleValueChange}
                  value={value ?? ""}
                  {...(disabled ||
                     (!isVisible(element) && {
                        onKeyDown: handleInputBlockade,
                        onPaste: handleInputBlockade,
                        onCut: handleInputBlockade,
                     }))}
               >
                  {(inputProps) => (
                     <Input
                        {...inputProps}
                        disabled={disabled}
                        className={`text-field__input field_${element.tech_name} _type_${element?.type}_type_ _id_${element.id}_id_ ${infoClass} has-alert-${element.hasAlert}`}
                        key={"text_" + element.tech_name}
                     />
                  )}
               </InputMask>
            ) : element.enum === "int" || element.enum === "dec" ? (
               <BP2Input
                  type="number"
                  className={`text-field__input field_${element.tech_name} _type_${element?.type}_type_ _id_${element.id}_id_ ${infoClass} has-alert-${element.hasAlert} _prec_${element.precision}_prec_ _enum_${element.enum}_enum_`}
                  min={element.object.UIP_NUM5 ? +element.object.UIP_NUM5 : -Infinity}
                  max={element.object.UIP_NUM6 ? +element.object.UIP_NUM6 : Infinity}
                  precision={+element.precision}
                  key={"text_" + element.tech_name}
                  onBlur={blurHandle}
                  disabled={disabled}
                  onChange={handleNumberInputChange}
                  value={value}
                  {...(disabled ||
                     (!isVisible(element) && {
                        onKeyDown: handleInputBlockade,
                        onPaste: handleInputBlockade,
                        onCut: handleInputBlockade,
                     }))}
               />
            ) : element.tech_name === "payment_url" ? (
               <Button
                  buttonType="primary"
                  className={`text-field__btn field_${element.tech_name}`}
                  id={`field-${element.tech_name}`}
                  {...(disabled ||
                     (!isVisible(element) && {
                        onKeyDown: handleInputBlockade,
                        onPaste: handleInputBlockade,
                        onCut: handleInputBlockade,
                     }))}
               >
                  {element?.label}
               </Button>
            ) : (
               <>
                  <BP2Input
                     className={`text-field__input field_${element.tech_name} _type_${element?.type}_type_ _id_${element.id}_id_ ${infoClass} has-alert-${element.hasAlert}`}
                     id={`field-${element.tech_name}`}
                     disabled={disabled}
                     onBlur={blurHandle}
                     onChange={handleValueChange}
                     value={value}
                  />
               </>
            )}
            {element.FormLoContent.seeking_out_type === "3" && advancedSearchListId ? (
               <DedicatedListButton
                  getValueFromAdvancedSearch={handleValueChange}
                  disabled={disabled}
                  listId={advancedSearchListId}
                  form={form}
                  fieldTechname={element.tech_name}
               />
            ) : null}

            {renderDevInfo(element)}
            <FieldInfo text={infoText} />
         </Form.Item>
      );
   },
);

TextField.propTypes = {
   element: PropTypes.object.isRequired,
   disabled: PropTypes.bool,
   isVisible: PropTypes.func,
   form: PropTypes.any,
   formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   insideGrid: PropTypes.bool,
   initialValue: PropTypes.string,
};
export default TextField;
